import { Component, computed, effect, inject, input, model, output, signal, untracked } from "@angular/core";
import { Dialog } from "@angular/cdk/dialog";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from "@angular/forms";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { v4 as uuidv4 } from "uuid";
import { CONSTANTS } from "../../../../../../constants";
import { Area, ImageType, Media, Zone } from "../../../../../../core/database/db";
import { ADD_NEW_ROOM_AREA, UNASSIGNED_AREA } from "../../../../../areas/models/AreaDTO";
import { AreaConfigurationDTO } from "../../../../../areas/models/configuration/AreaConfigurationDTO";
import { BqaImageItem } from "../../../../../media/components/images-modal/bqa-image-item";
import { ImagesModalComponent } from "../../../../../media/components/images-modal/images-modal.component";
import { ImageCompressionPipelineService, MediaForCompression } from "../../../../../media/services/image-compression-pipeline.service";
import { insertAt } from "../../../../../media/utils/media-order.util";
import { FormFieldComponent } from "../../../../../../shared/components/form-field/form-field.component";
import { ModalIcon, UiModalService } from "../../../../../../shared/components/modal/ui-modal.service";
import { PhotoCaptureComponent } from "../../../../../../shared/components/photo-capture/photo-capture.component";
import { SectionHeaderComponent } from "../../../../../../shared/components/section-header/section-header.component";
import { StandardBottomSheetComponent } from "../../../../../../shared/components/standard-bottom-sheet/standard-bottom-sheet.component";
import { SelectFieldComponent } from "../../../../../../shared/components/select-field/select-field.component";
import { TextareaComponent } from "../../../../../../shared/components/textarea/textarea.component";
import { VerticalButton, VerticalButtonGroupComponent } from "../../../../../../shared/components/vertical-button-group/vertical-button-group.component";
import { BlobService } from "../../../../../../shared/services/blob.service";
import { AddAreaDialogComponent, AddAreaSubmit } from "../../../job-areas/add-area/add-area-dialog.component";

export type ChecklistIssueSaveMode = "return" | "addAnother";

/** `create` logs a brand new issue; `edit` reworks the photos and notes of one already logged. */
export type ChecklistIssueSheetMode = "create" | "edit";

export interface ChecklistIssueSubmit {
  notes: string;
  media: Media[];
  mode: ChecklistIssueSaveMode;
  lotAreaIdentifier?: string;
}

/**
 * Needs Attention form for the BQ2/BQ3/BQ4 checklist.
 *
 * Mirrors `app-job-needs-attention` but as a bottom sheet and reduced to the two fields the
 * single-area steps capture: photos and notes, both required.
 */
@Component({
  selector: "app-checklist-issue-sheet",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    StandardBottomSheetComponent,
    SectionHeaderComponent,
    FormFieldComponent,
    PhotoCaptureComponent,
    SelectFieldComponent,
    TextareaComponent,
    VerticalButtonGroupComponent,
    AddAreaDialogComponent
  ],
  templateUrl: "./checklist-issue-sheet.component.html",
  styleUrl: "./checklist-issue-sheet.component.scss"
})
export class ChecklistIssueSheetComponent {
  private readonly dialog = inject(Dialog);
  private readonly blobService = inject(BlobService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly modalService = inject(UiModalService);
  protected readonly imageCompressionPipelineService = inject(ImageCompressionPipelineService);

  isOpen = model<boolean>(false);
  /** Checklist item being flagged; rendered as the sheet's context line (may contain markup). */
  assertionText = input<string>("");
  saving = input<boolean>(false);
  mode = input<ChecklistIssueSheetMode>("create");
  /** Notes already logged against the issue being edited. */
  initialNotes = input<string>("");
  /** Photos already logged against the issue being edited. */
  initialMedia = input<Media[]>([]);
  /** Room to pre-select: assigned room when editing, or last-used room when creating. */
  initialLotAreaIdentifier = input<string | undefined>(undefined);
  /** Identifier new photos are parented to; required in edit mode so they land on the existing issue. */
  parentIdentifier = input<string>("");
  roomOptions = input<Area[]>([]);
  /** Offers "Add New Room" in the picker; only the steps that assign lot rooms (BQ3/BQ4) set this. */
  canAddRoom = input<boolean>(false);
  /** Room types offered by the Add a Room sheet. */
  areasConfig = input<AreaConfigurationDTO[] | undefined>(undefined);
  /** Zones the new room can belong to (Interior/Exterior). */
  zoneOptions = input<Zone[]>([]);
  savingRoom = input<boolean>(false);
  /** Set by the parent once a room has been created; selects it and returns to this form. */
  createdRoomIdentifier = input<string | undefined>(undefined);

  readonly submitted = output<ChecklistIssueSubmit>();
  readonly cancelled = output<void>();
  /** Only ever raised from edit mode: a brand new issue has nothing to delete yet. */
  readonly deleteRequested = output<void>();
  readonly roomSubmitted = output<AddAreaSubmit>();

  protected readonly maxNoteLength = CONSTANTS.CONSTRAINTS.MAX_NOTE_LENGTH;
  protected readonly maxPhotos = CONSTANTS.CONSTRAINTS.MAX_PHOTOS;

  protected readonly title = computed(() => (this.mode() === "edit" ? "Edit Issue" : "Needs Attention"));

  protected formSubmitted = signal(false);
  protected photos = signal<Media[]>([]);
  protected savingMode = signal<ChecklistIssueSaveMode | null>(null);
  protected addRoomSheetOpen = signal(false);
  /** Saved photos the CM removed; sent back with `deleted` set so the service soft-deletes them. */
  private removedPhotos = signal<Media[]>([]);
  /** Last real room choice, restored when the CM picks the "Add New Room" entry. */
  private lastSelectedRoomIdentifier: string | null = null;

  /** "Add New Room" is an action, so it counts as no room picked rather than a savable answer. */
  private readonly roomSelectionValidator = (control: AbstractControl): ValidationErrors | null =>
    (control.value === ADD_NEW_ROOM_AREA.identifier ? { required: true } : null);

  protected form = new FormGroup({
    lotAreaIdentifier: new FormControl<string | null>(null),
    notes: new FormControl("", { nonNullable: true, validators: [Validators.required, Validators.maxLength(CONSTANTS.CONSTRAINTS.MAX_NOTE_LENGTH)] }),
    photos: new FormControl<Media[]>([], { nonNullable: true, validators: [control => (control.value.length ? null : { required: true })] })
  });

  protected showRoomField = computed(() => this.roomOptions().length > 0 || this.canAddRoom());
  protected areas = computed(() => {
    const rooms = [...this.roomOptions()].sort((a, b) => a.name.localeCompare(b.name));
    const options = [...rooms, UNASSIGNED_AREA];

    if (this.canAddRoom()) {
      options.push(ADD_NEW_ROOM_AREA);
    }

    return options;
  });

  /** Photos are encoded before the issue exists, so they hang off a placeholder identifier until save. */
  private itemIdentifier = uuidv4();
  private objectUrls = new Set<string>();

  protected galleryItems = computed(() =>
    this.photos()
      .filter(image => image.base64Image || image.resourceUri)
      .map(image => new BqaImageItem({
        azureBlobUri: this.toObjectUrl(image),
        mediaIdentifier: image.fileIdentifier,
        isThumbnailOnly: image.isThumbnailOnly || false
      })));

  protected photoThumbnails = computed<SafeUrl[]>(() =>
    this.galleryItems().map(item => this.sanitizer.bypassSecurityTrustUrl(item.imageData.azureBlobUri)));

  constructor() {
    // untracked so a later change to the sheet's own inputs cannot wipe the CM's work mid-session.
    effect(() => {
      if (this.isOpen()) {
        untracked(() => this.resetForm());
      }
    });

    effect(() => {
      const control = this.form.controls.lotAreaIdentifier;
      if (this.showRoomField()) {
        control.setValidators([Validators.required, this.roomSelectionValidator]);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    });

    this.form.controls.lotAreaIdentifier.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value => {
        if (value === ADD_NEW_ROOM_AREA.identifier) {
          this.addRoomSheetOpen.set(true);
          return;
        }

        this.lastSelectedRoomIdentifier = value;
      });

    effect(() => {
      const identifier = this.createdRoomIdentifier();
      if (!identifier) {
        return;
      }

      untracked(() => {
        this.form.controls.lotAreaIdentifier.setValue(identifier);
        this.addRoomSheetOpen.set(false);
      });
    });
  }

  protected get footerButtons(): VerticalButton[] {
    const compressing = this.imageCompressionPipelineService.isCompressingImages();
    const busy = compressing || this.saving();

    if (this.mode() === "edit") {
      let editLabel = "Save Changes";
      if (compressing) {
        editLabel = "Optimizing Images...";
      } else if (this.savingMode() === "return") {
        editLabel = "Saving...";
      }

      return [
        {
          label: editLabel,
          severity: "contrast",
          disabled: busy,
          styleClass: "bqa-primary-btn",
          action: () => this.save("return"),
          testId: "checklist-issue-sheet-btn-save"
        },
        {
          label: "Delete Issue",
          severity: "danger",
          outlined: true,
          disabled: busy,
          styleClass: "bqa-danger-btn",
          action: () => this.confirmDelete(),
          testId: "checklist-issue-sheet-btn-delete"
        },
        {
          label: "Cancel",
          severity: "secondary",
          text: true,
          styleClass: "bqa-secondary-btn",
          action: () => this.onDismissRequested(),
          testId: "checklist-issue-sheet-btn-cancel"
        }
      ];
    }

    let saveLabel = "Save Item";
    if (compressing) {
      saveLabel = "Optimizing Images...";
    } else if (this.savingMode() === "return") {
      saveLabel = "Saving...";
    }

    return [
      {
        label: saveLabel,
        severity: "contrast",
        disabled: busy,
        styleClass: "bqa-primary-btn",
        action: () => this.save("return"),
        testId: "checklist-issue-sheet-btn-save"
      },
      {
        label: this.savingMode() === "addAnother" ? "Saving..." : "Save & Add Another Issue",
        severity: "contrast",
        outlined: true,
        disabled: busy,
        styleClass: "bqa-primary-btn",
        action: () => this.save("addAnother"),
        testId: "checklist-issue-sheet-btn-save-add-another"
      },
      {
        label: "Cancel",
        severity: "secondary",
        text: true,
        styleClass: "bqa-secondary-btn",
        action: () => this.onDismissRequested(),
        testId: "checklist-issue-sheet-btn-cancel"
      }
    ];
  }

  /** Drives the base sheet's click-off / swipe-down guard: only hold the sheet when there is work to lose. */
  protected get hasUnsavedChanges(): boolean {
    return this.removedPhotos().length > 0
      || this.photos().some(photo => !photo.id)
      || this.form.controls.notes.value.trim() !== this.initialNotes().trim()
      || (this.form.controls.lotAreaIdentifier.value ?? undefined) !== this.initialRoom();
  }

  protected isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control?.invalid && (control.touched || this.formSubmitted()));
  }

  protected async onPhotosSelected(files: File[], insertIndex?: number): Promise<Media[]> {
    if (files.length === 0) {
      return [];
    }

    const mediaRecords = await this.imageCompressionPipelineService.encode(files, this.itemIdentifier, ImageType.Issue);
    this.photos.update(current => insertAt(current, mediaRecords, insertIndex));
    this.syncPhotosControl();

    this.imageCompressionPipelineService.compress(mediaRecords, async compressed => {
      this.photos.update(current => current.map(image => {
        const match = compressed.find(m => m.fileIdentifier === image.fileIdentifier);
        return match
          ? { ...image, base64Image: match.base64Image, size: match.size, mimeType: match.mimeType }
          : image;
      }));
      this.syncPhotosControl();
    });

    return mediaRecords;
  }

  protected openImagesModal(index: number): void {
    this.dialog.open<string>(ImagesModalComponent, {
      data: {
        images: this.galleryItems(),
        index,
        isIssue: true,
        onDeleted: (fileIdentifier: string) => this.handleImageDeleted(fileIdentifier),
        onAnnotated: (fileIdentifier: string, file: File) => this.handleImageAnnotated(fileIdentifier, file),
        backdropClass: "images-modal-backdrop"
      }
    });
  }

  private async handleImageAnnotated(originalFileIdentifier: string, annotatedFile: File): Promise<string | undefined> {
    const index = this.photos().findIndex(image => image.fileIdentifier === originalFileIdentifier);
    this.handleImageDeleted(originalFileIdentifier);
    const [added] = await this.onPhotosSelected([annotatedFile], index);
    return added?.fileIdentifier;
  }

  protected onSheetOpenChange(isOpen: boolean): void {
    if (!isOpen && this.isOpen()) {
      this.cancel();
    }
  }

  /**
   * Restores the previous room when the CM backs out of adding one.
   */
  protected onAddRoomSheetVisibleChange(isVisible: boolean): void {
    this.addRoomSheetOpen.set(isVisible);

    if (!isVisible && this.form.controls.lotAreaIdentifier.value === ADD_NEW_ROOM_AREA.identifier) {
      this.form.controls.lotAreaIdentifier.setValue(this.lastSelectedRoomIdentifier);
    }
  }

  /**
   * Reached from Cancel, and from a backdrop tap or swipe-down while the form has input (those
   * gestures close the sheet on their own once it is empty).
   */
  protected onDismissRequested(): void {
    if (!this.hasUnsavedChanges) {
      this.cancel();
      return;
    }

    this.modalService.showModal({
      icon: ModalIcon.None,
      header: "Cancel",
      subHeader: "Are you sure you want to proceed?",
      message: "Unsaved changes will be lost.",
      cancelLabel: "No",
      onCancel: () => { },
      actionLabel: "Yes",
      onAction: () => this.cancel(),
      isDestructive: true
    });
  }

  private confirmDelete(): void {
    this.modalService.showModal({
      icon: ModalIcon.None,
      header: "Delete Issue",
      subHeader: "Are you sure you want to proceed?",
      message: "The issue and its photos will be removed.",
      cancelLabel: "No",
      onCancel: () => { },
      actionLabel: "Yes",
      onAction: () => this.deleteRequested.emit(),
      isDestructive: true
    });
  }

  private handleImageDeleted(fileIdentifier: string): void {
    const removed = this.photos().find(image => image.fileIdentifier === fileIdentifier);
    if (removed?.id) {
      this.removedPhotos.update(current => [...current, { ...removed, deleted: 1 }]);
    }

    this.photos.update(current => current.filter(image => image.fileIdentifier !== fileIdentifier));
    this.syncPhotosControl(true);
  }

  private async save(mode: ChecklistIssueSaveMode): Promise<void> {
    this.formSubmitted.set(true);
    if (this.form.invalid) {
      return;
    }

    this.savingMode.set(mode);
    await this.imageCompressionPipelineService.waitForCompletion();

    const media = [...this.photos(), ...this.removedPhotos()].map(image => {
      const { file: _file, ...rest } = image as MediaForCompression;
      return rest;
    });

    this.submitted.emit({
      notes: this.form.controls.notes.value.trim(),
      media,
      mode,
      lotAreaIdentifier: this.showRoomField()
        ? (this.form.controls.lotAreaIdentifier.value ?? undefined)
        : undefined
    });
  }

  /** Called by the parent once the save succeeded, so a failed save keeps the CM's input. */
  resetForNextIssue(savedLotAreaIdentifier: string | null): void {
    this.savingMode.set(null);
    this.resetForm(savedLotAreaIdentifier, true);
  }

  private cancel(): void {
    this.isOpen.set(false);
    this.cancelled.emit();
  }

  private resetForm(lotAreaIdentifierOverride?: string | null, useLotAreaIdentifierOverride = false): void {
    this.releaseObjectUrls();
    this.itemIdentifier = this.parentIdentifier() || uuidv4();
    this.formSubmitted.set(false);
    this.savingMode.set(null);
    this.removedPhotos.set([]);
    this.photos.set(this.mode() === "edit" ? [...this.initialMedia()] : []);
    const lotAreaIdentifier = useLotAreaIdentifierOverride
      ? (lotAreaIdentifierOverride ?? null)
      : (this.initialRoom() ?? null);
    this.form.reset({
      lotAreaIdentifier,
      notes: this.mode() === "edit" ? this.initialNotes() : "",
      photos: this.photos()
    });
    this.syncPhotosControl();
  }

  /** An edited issue with no room maps to the explicit Unassigned option, so the required field is satisfied. */
  private initialRoom(): string | undefined {
    const room = this.initialLotAreaIdentifier();
    if (this.mode() === "edit") {
      return room ?? UNASSIGNED_AREA.identifier;
    }

    return room;
  }

  private syncPhotosControl(markAsTouched = false): void {
    this.form.controls.photos.setValue(this.photos(), { emitEvent: false });
    if (markAsTouched) {
      this.form.controls.photos.markAsTouched();
    }
  }

  private toObjectUrl(image: Media): string {
    if (!image.base64Image) {
      return image.resourceUri ?? "";
    }

    const url = URL.createObjectURL(this.blobService.base64ToBlob(image.base64Image));
    this.objectUrls.add(url);
    return url;
  }

  private releaseObjectUrls(): void {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
    this.objectUrls.clear();
  }
}
